import { useEffect, useMemo, useState } from 'react';
import { getAllPartners } from '../../services/partnerService';
import {
  getMyCertifications,
  createCertification,
  updateCertification,
  deleteCertification,
} from '../../services/certificationService';
import './MemberCertifications.css';

const statusLabel = {
  ACTIVE: 'ACTIVE',
  EXPIRING_SOON: 'EXPIRING SOON',
  EXPIRED: 'EXPIRED',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

export default function MemberCertifications() {
  const [partners, setPartners] = useState([]);
  const [certifications, setCertifications] = useState([]);

  const [partnerId, setPartnerId] = useState('');
  const [certificationTypeId, setCertificationTypeId] = useState('');
  const [completedDate, setCompletedDate] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const selectedPartner = useMemo(
    () => partners.find((partner) => partner.id === partnerId),
    [partners, partnerId]
  );

  const availableCertificationTypes = selectedPartner?.certificationTypes || [];

  const selectedCertificationType = useMemo(
    () =>
      availableCertificationTypes.find(
        (certificationType) => certificationType.id === certificationTypeId
      ),
    [availableCertificationTypes, certificationTypeId]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [partnersData, certificationsData] = await Promise.all([
        getAllPartners(),
        getMyCertifications(),
      ]);

      const activePartners = (partnersData || []).filter(
        (partner) => partner.status === 'ACTIVE'
      );

      setPartners(activePartners);
      setCertifications(certificationsData || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Unable to load your certifications right now.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handlePartnerChange = (event) => {
    setPartnerId(event.target.value);
    setCertificationTypeId('');
  };

  const handleAdd = async (event) => {
    event.preventDefault();

    if (!certificationTypeId || !completedDate) {
      setError('Please select a certification and completion date.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const data = await createCertification({
        certificationTypeId,
        completedDate,
      });

      setCertifications((current) => [
        data?.cert || data,
        ...current,
      ]);

      setPartnerId('');
      setCertificationTypeId('');
      setCompletedDate('');
      setMessage(data?.message || 'Certification added successfully.');
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Failed to add certification.'
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (certification) => {
    setEditingId(certification.id);
    setEditDate(toInputDate(certification.completedDate));
    setError('');
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDate('');
  };

  const saveEdit = async (id) => {
    if (!editDate) {
      setError('Please select a completion date.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const data = await updateCertification(id, {
        completedDate: editDate,
      });

      const updated = data?.cert || data;

      setCertifications((current) =>
        current.map((certification) =>
          certification.id === id ? updated : certification
        )
      );

      cancelEdit();
      setMessage(data?.message || 'Certification updated successfully.');
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Failed to update certification.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certification record?')) return;

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const data = await deleteCertification(id);

      setCertifications((current) =>
        current.filter((certification) => certification.id !== id)
      );

      if (editingId === id) cancelEdit();

      setMessage(data?.message || 'Certification deleted.');
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Failed to delete certification.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="member-cert-page">
      <div className="member-cert-shell">
        <header className="member-cert-hero">
          <div className="member-cert-eyebrow">
            <span className="member-cert-live-dot" aria-hidden="true" />
            Member portal
          </div>

          <h1>My Certifications</h1>
          <p>
            Keep your professional certifications up to date and track their
            current validity in one place.
          </p>
        </header>

        {message ? (
          <div className="member-cert-alert member-cert-alert--success">
            {message}
          </div>
        ) : null}

        {error ? (
          <div className="member-cert-alert member-cert-alert--error">
            {error}
          </div>
        ) : null}

        <div className="member-cert-layout">
          <section className="member-cert-card member-cert-add-card">
            <div className="member-cert-card-heading">
              <div>
                <span className="member-cert-kicker">01 / RECORD</span>
                <h2>Add certification</h2>
              </div>
            </div>

            <form onSubmit={handleAdd} className="member-cert-form">
              <label>
                <span>Partner</span>
                <select
                  value={partnerId}
                  onChange={handlePartnerChange}
                  disabled={loading || saving}
                  required
                >
                  <option value="">Select partner</option>
                  {partners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Certification</span>
                <select
                  value={certificationTypeId}
                  onChange={(event) =>
                    setCertificationTypeId(event.target.value)
                  }
                  disabled={!partnerId || saving}
                  required
                >
                  <option value="">
                    {partnerId
                      ? 'Select certification'
                      : 'Select a partner first'}
                  </option>
                  {availableCertificationTypes.map((certificationType) => (
                    <option
                      key={certificationType.id}
                      value={certificationType.id}
                    >
                      {certificationType.name}
                    </option>
                  ))}
                </select>
              </label>

              {selectedCertificationType ? (
                <div className="member-cert-validity">
                  <span>Validity</span>
                  <strong>
                    {selectedCertificationType.validityMonths
                      ? `${selectedCertificationType.validityMonths} months`
                      : 'No expiry'}
                  </strong>
                </div>
              ) : null}

              <label>
                <span>Completion date</span>
                <input
                  type="date"
                  value={completedDate}
                  onChange={(event) => setCompletedDate(event.target.value)}
                  disabled={saving}
                  required
                />
              </label>

              <button
                className="member-cert-primary-btn"
                type="submit"
                disabled={saving || loading || !certificationTypeId}
              >
                {saving ? 'Saving...' : 'Add certification'}
              </button>
            </form>

            <p className="member-cert-note">
              Expiry is calculated automatically from the certification's
              configured validity period.
            </p>
          </section>

          <section className="member-cert-card member-cert-list-card">
            <div className="member-cert-card-heading member-cert-card-heading--row">
              <div>
                <span className="member-cert-kicker">02 / YOUR RECORDS</span>
                <h2>Certification history</h2>
              </div>

              <button
                type="button"
                className="member-cert-refresh-btn"
                onClick={loadData}
                disabled={loading || saving}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {loading ? (
              <div className="member-cert-empty">
                <span className="member-cert-loader" />
                Loading your certifications...
              </div>
            ) : certifications.length === 0 ? (
              <div className="member-cert-empty">
                <div className="member-cert-empty-mark">/</div>
                <h3>No certifications yet</h3>
                <p>
                  Add your first completed certification using the form.
                </p>
              </div>
            ) : (
              <div className="member-cert-records">
                {certifications.map((certification) => {
                  const type = certification.certificationType;
                  const partner = type?.partner;
                  const isEditing = editingId === certification.id;

                  return (
                    <article
                      key={certification.id}
                      className="member-cert-record"
                    >
                      <div className="member-cert-record-main">
                        <div className="member-cert-partner">
                          {partner?.logo ? (
                            <img
                              src={partner.logo}
                              alt=""
                              className="member-cert-logo"
                            />
                          ) : (
                            <div className="member-cert-logo member-cert-logo--fallback">
                              {(partner?.name || '?').charAt(0)}
                            </div>
                          )}

                          <div>
                            <div className="member-cert-partner-name">
                              {partner?.name || 'Partner'}
                            </div>
                            <h3>{type?.name || 'Certification'}</h3>
                          </div>
                        </div>

                        <span
                          className={`member-cert-status member-cert-status--${(
                            certification.status || 'ACTIVE'
                          ).toLowerCase()}`}
                        >
                          {statusLabel[certification.status] ||
                            certification.status ||
                            'ACTIVE'}
                        </span>
                      </div>

                      {isEditing ? (
                        <div className="member-cert-edit">
                          <label>
                            <span>Completion date</span>
                            <input
                              type="date"
                              value={editDate}
                              onChange={(event) =>
                                setEditDate(event.target.value)
                              }
                              disabled={saving}
                            />
                          </label>

                          <div className="member-cert-edit-actions">
                            <button
                              type="button"
                              className="member-cert-primary-btn"
                              onClick={() => saveEdit(certification.id)}
                              disabled={saving}
                            >
                              {saving ? 'Saving...' : 'Save changes'}
                            </button>
                            <button
                              type="button"
                              className="member-cert-secondary-btn"
                              onClick={cancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="member-cert-meta-grid">
                            <div>
                              <span>Completed</span>
                              <strong>
                                {formatDate(certification.completedDate)}
                              </strong>
                            </div>
                            <div>
                              <span>Expires</span>
                              <strong>
                                {formatDate(certification.expiryDate)}
                              </strong>
                            </div>
                          </div>

                          <div className="member-cert-record-actions">
                            <button
                              type="button"
                              className="member-cert-secondary-btn"
                              onClick={() => startEdit(certification)}
                              disabled={saving}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="member-cert-danger-btn"
                              onClick={() =>
                                handleDelete(certification.id)
                              }
                              disabled={saving}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}
